/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { IFaxFolder } from '@dataTypes/fax.type'
import {
  createFaxFolder,
  deleteFaxFolderById,
  getAllFaxFolder,
  updateFaxFolderById,
} from '@services/api/fax/folder.api'
import { RootState } from '@store/index'

export const fetchAllFaxFolders = createAsyncThunk<
  IFaxFolder[],
  void | { dbName?: string },
  { rejectValue: string }
>('api/fax/folder/fetch/all', async (arg, { rejectWithValue }) => {
  try {
    const dbName =
      typeof arg === 'object' && arg?.dbName != null ? arg.dbName : undefined
    const response = await getAllFaxFolder(dbName)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'fax_folder' in response.data &&
      Array.isArray(response.data.fax_folder)
    ) {
      return response.data.fax_folder as IFaxFolder[]
    }

    return rejectWithValue('Fax folders not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addFaxFolder = createAsyncThunk<
  IFaxFolder,
  IFaxFolder,
  { rejectValue: string }
>(
  'api/fax/folder/add',
  async (data, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const clinicId = state.clinic.selectedClinic?.clinic_id as string
      const response = await createFaxFolder({
        ...data,
        clinic_id: clinicId,
      })
      if (response.statusCode !== 201) {
        return rejectWithValue(response.message || 'Failed to add fax folder')
      }
      if (
        response.data &&
        'fax_folder' in response.data &&
        response.data.fax_folder
      ) {
        const folder = response.data.fax_folder as IFaxFolder
        await dispatch(fetchAllFaxFolders())
        return folder
      }
      return rejectWithValue('Fax folder not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create fax folder')
    }
  },
)

export const updateFaxFolder = createAsyncThunk<
  IFaxFolder,
  { fax_folder_id: string; data: Partial<IFaxFolder> },
  { rejectValue: string }
>(
  'api/fax/folder/update',
  async ({ fax_folder_id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateFaxFolderById(fax_folder_id, data)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      if (
        response.data &&
        'fax_folder' in response.data &&
        response.data.fax_folder
      ) {
        await dispatch(fetchAllFaxFolders())
        return response.data.fax_folder as IFaxFolder
      }
      return rejectWithValue('Fax folder not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteFaxFolder = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/fax/folder/delete', async (id, { dispatch, rejectWithValue }) => {
  try {
    const response = await deleteFaxFolderById(id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    await dispatch(fetchAllFaxFolders())
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})
